import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Camera, RefreshCw, X } from "lucide-react";

// Ambil foto langsung dari kamera perangkat (bukan galeri).
export const CameraCapture = ({ onCapture, dataTestId = "camera", watermark = [] }) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState("");
  const [shot, setShot] = useState(null);

  const start = async () => {
    setError("");
    setShot(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" }, audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.setAttribute("playsinline", "true");
        await videoRef.current.play();
      }
      setReady(true);
    } catch (e) {
      setReady(false);
      setError("Kamera tidak tersedia atau izin ditolak. Absensi wajib menggunakan kamera.");
    }
  };

  const stop = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  useEffect(() => { start(); return () => stop(); /* eslint-disable-next-line */ }, []);

  const capture = () => {
    const v = videoRef.current;
    if (!v) return;
    const canvas = document.createElement("canvas");
    canvas.width = v.videoWidth || 640;
    canvas.height = v.videoHeight || 480;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
    if (watermark && watermark.length) {
      const fs = Math.max(14, Math.round(canvas.width / 30));
      const lh = fs + 6;
      const bh = lh * watermark.length + 12;
      ctx.fillStyle = "rgba(0,0,0,0.55)";
      ctx.fillRect(0, canvas.height - bh, canvas.width, bh);
      ctx.fillStyle = "#ffffff";
      ctx.font = `600 ${fs}px sans-serif`;
      watermark.forEach((line, i) => ctx.fillText(String(line), 12, canvas.height - bh + 10 + fs + i * lh));
    }
    canvas.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      setShot(url);
      stop();
      setReady(false);
      onCapture(blob);
    }, "image/jpeg", 0.85);
  };

  const retake = () => { setShot(null); onCapture(null); start(); };

  return (
    <div className="space-y-2" data-testid={dataTestId}>
      <div className="relative overflow-hidden rounded-xl border border-border bg-black/80 aspect-[4/3] grid place-items-center">
        {!shot && <video ref={videoRef} playsInline muted className="h-full w-full object-cover" data-testid={`${dataTestId}-video`} />}
        {shot && <img src={shot} alt="Foto absensi" className="h-full w-full object-cover" data-testid={`${dataTestId}-preview`} />}
        {error && (
          <div className="absolute inset-0 grid place-items-center p-4 text-center">
            <div className="flex flex-col items-center gap-2 text-white">
              <X className="h-8 w-8 text-rose-400" />
              <p className="text-xs" data-testid={`${dataTestId}-error`}>{error}</p>
              <Button size="sm" variant="outline" onClick={start} data-testid={`${dataTestId}-retry`}>Coba Lagi</Button>
            </div>
          </div>
        )}
      </div>
      <div className="flex gap-2">
        {!shot && ready && (
          <Button type="button" variant="gold" className="flex-1 gap-1.5" onClick={capture} data-testid={`${dataTestId}-snap`}>
            <Camera className="h-4 w-4" /> Ambil Foto
          </Button>
        )}
        {shot && (
          <Button type="button" variant="outline" className="flex-1 gap-1.5" onClick={retake} data-testid={`${dataTestId}-retake`}>
            <RefreshCw className="h-4 w-4" /> Ambil Ulang
          </Button>
        )}
      </div>
    </div>
  );
};
